import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Calendar, Coffee, Dices, Search, TrendingUp } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { formatLocalYmd } from '../lib/dates';
import { useUser } from '../hooks/useUser';
import { getMedalLedger } from '../lib/medals';
import { getNewsUnreadCount, NEWS_SEEN_KEY } from '../lib/news';
import { fetchTopicPlayCounts } from '../lib/topicPlays';
import { archivePath } from '../lib/archivePaths';
import { PageSeo } from './PageSeo';
import { BackToToday, NarrowPage, PageHeader } from './SiteChrome';
import { SiteFooter } from './SiteFooter';
import { PAGE_META } from '../lib/seo';

export type ArchiveTopic = {
  id: string;
  title: string;
  play_date: string;
  description?: string | null;
};

type MonthGroup = {
  key: string;
  label: string;
  topics: ArchiveTopic[];
};

function formatDisplayDate(dateStr: string) {
  const [y, m, d] = dateStr.split('-');
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function monthLabel(dateStr: string) {
  const [y, m] = dateStr.split('-');
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function ArchiveTopicRow({
  topic,
  rank,
  siblings,
}: {
  topic: ArchiveTopic;
  rank?: number;
  siblings: ArchiveTopic[];
}) {
  const [y, m, d] = topic.play_date.split('-').map(Number);
  const date = new Date(y, m - 1, d);

  return (
    <Link
      to={archivePath(topic, siblings)}
      className="group w-full text-left rounded-2xl border border-border bg-background/60 hover:bg-muted/70 hover:border-foreground/20 transition-all active:scale-[0.99] px-4 py-4 flex items-start gap-3 sm:gap-4"
    >
      {typeof rank === 'number' ? (
        <div
          className={`shrink-0 w-10 h-10 mt-0.5 rounded-2xl font-black tabular-nums flex items-center justify-center ${
            rank === 1
              ? 'text-black bg-[linear-gradient(to_bottom,#CD7F32,#facc15,#22d3ee)]'
              : 'bg-muted text-foreground/70'
          }`}
        >
          {rank}
        </div>
      ) : (
        <div className="shrink-0 w-12 sm:w-14 text-center pt-0.5">
          <div className="text-[10px] font-black uppercase tracking-widest text-foreground/40">
            {date.toLocaleDateString(undefined, { weekday: 'short' })}
          </div>
          <div className="text-xl font-black tabular-nums leading-none mt-0.5">{d}</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-foreground/40 mt-0.5">
            {date.toLocaleDateString(undefined, { month: 'short' })}
          </div>
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="font-bold text-base sm:text-lg leading-snug break-words group-hover:text-foreground">
          {topic.title}
        </div>
        {topic.description ? (
          <div className="text-sm text-foreground/55 line-clamp-2 mt-1">{topic.description}</div>
        ) : (
          <div className="text-xs font-semibold uppercase tracking-widest text-foreground/35 mt-1">
            {formatDisplayDate(topic.play_date)}
          </div>
        )}
      </div>

      <div className="shrink-0 w-9 h-9 mt-0.5 rounded-full border border-border bg-muted/50 group-hover:bg-foreground group-hover:text-background group-hover:border-foreground flex items-center justify-center transition-colors">
        <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

function collectLocalPlayedTopicIds(userId: string | null, topicIds: string[]): Set<string> {
  const played = new Set<string>();
  if (typeof window === 'undefined') return played;

  if (userId) {
    try {
      for (const id of getMedalLedger(userId).recordedTopicIds) played.add(id);
    } catch {
      // ignore unreadable medal ledger
    }
  }

  for (const id of topicIds) {
    try {
      const raw = localStorage.getItem(`centumth_guesses_${id}`);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length >= 5) played.add(id);
    } catch {
      // ignore unreadable guess history
    }
  }

  return played;
}

export function ArchivePage({ initialTopics }: { initialTopics?: ArchiveTopic[] } = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userId } = useUser();
  const [topics, setTopics] = useState<ArchiveTopic[]>(initialTopics ?? []);
  const [loading, setLoading] = useState(!initialTopics);
  const [query, setQuery] = useState('');
  const [newsUnreadCount, setNewsUnreadCount] = useState(0);
  const [playedTopicIds, setPlayedTopicIds] = useState<Set<string>>(() => new Set());
  const [playCounts, setPlayCounts] = useState<Map<string, number>>(() => new Map());
  const sortPopular = searchParams.get('sort') === 'popular';

  useEffect(() => {
    try {
      setNewsUnreadCount(getNewsUnreadCount(localStorage.getItem(NEWS_SEEN_KEY)));
    } catch {
      setNewsUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    if (initialTopics) return;
    async function loadArchive() {
      const today = formatLocalYmd();
      const { data } = await supabase
        .from('topics')
        .select('id, title, play_date, description')
        .lt('play_date', today)
        .order('play_date', { ascending: false });

      setTopics(data ?? []);
      setLoading(false);
    }
    loadArchive();
  }, [initialTopics]);

  useEffect(() => {
    let cancelled = false;
    async function loadCounts() {
      const counts = await fetchTopicPlayCounts();
      if (!cancelled) setPlayCounts(counts);
    }
    void loadCounts();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const topicIds = topics.map((t) => t.id);
    const localPlayed = collectLocalPlayedTopicIds(userId, topicIds);
    setPlayedTopicIds(localPlayed);
    if (!userId || topicIds.length === 0) return;

    let cancelled = false;
    async function loadRemotePlayed() {
      const { data } = await supabase
        .from('guesses')
        .select('topic_id')
        .eq('user_id', userId!)
        .eq('completed', true);

      if (cancelled) return;
      const next = new Set(localPlayed);
      for (const row of data ?? []) {
        if (row.topic_id) next.add(row.topic_id);
      }
      setPlayedTopicIds(next);
    }
    loadRemotePlayed();
    return () => {
      cancelled = true;
    };
  }, [userId, topics]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return topics;
    return topics.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q) ||
        t.play_date.includes(q) ||
        formatDisplayDate(t.play_date).toLowerCase().includes(q) ||
        monthLabel(t.play_date).toLowerCase().includes(q)
    );
  }, [topics, query]);

  const listed = useMemo(() => {
    if (!sortPopular) return filtered;
    return [...filtered].sort((a, b) => {
      const delta = (playCounts.get(b.id) ?? 0) - (playCounts.get(a.id) ?? 0);
      if (delta !== 0) return delta;
      return b.play_date.localeCompare(a.play_date);
    });
  }, [filtered, sortPopular, playCounts]);

  const groups = useMemo(() => {
    const map = new Map<string, MonthGroup>();
    for (const t of filtered) {
      const key = t.play_date.slice(0, 7);
      const existing = map.get(key);
      if (existing) {
        existing.topics.push(t);
      } else {
        map.set(key, { key, label: monthLabel(t.play_date), topics: [t] });
      }
    }
    return Array.from(map.values());
  }, [filtered]);

  const unplayedTopics = useMemo(
    () => topics.filter((t) => !playedTopicIds.has(t.id)),
    [topics, playedTopicIds]
  );

  const goToRandomUnplayed = () => {
    if (unplayedTopics.length === 0) return;
    const pick = unplayedTopics[Math.floor(Math.random() * unplayedTopics.length)];
    navigate(archivePath(pick, topics));
  };

  const setSortPopular = (on: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.set('sort', 'popular');
    else next.delete('sort');
    setSearchParams(next, { replace: true });
  };

  return (
    <NarrowPage>
    <div className="relative pb-8">
      <PageSeo meta={PAGE_META.archive} />
      <PageHeader newsUnreadCount={newsUnreadCount} />

      <div className="relative min-h-[calc(100vh-6rem)] overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-32 -left-24 w-80 h-80 rounded-full bg-[#22d3ee]/10 blur-3xl" />
          <div className="absolute top-1/3 -right-24 w-72 h-72 rounded-full bg-[#facc15]/10 blur-3xl" />
          <div className="absolute -bottom-24 left-1/3 w-64 h-64 rounded-full bg-[#4ade80]/[0.07] blur-3xl" />
        </div>

        <motion.div initial={false} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <BackToToday />
          <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-none mb-3">Archive</h1>
          <p className="text-foreground/60 leading-relaxed max-w-md">
            Replay any past topic. Same rules — five guesses, chase the answers closest to #100.
          </p>
          {!loading && topics.length > 0 && (
            <div className="mt-3 text-xs font-bold uppercase tracking-widest text-foreground/40">
              {topics.length} topic{topics.length === 1 ? '' : 's'}
            </div>
          )}
        </motion.div>

        <motion.a
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          href="https://ko-fi.com/eliass_al"
          target="_blank"
          rel="noopener noreferrer"
          className="mb-5 flex items-center justify-between gap-3 rounded-2xl px-4 py-3.5 text-black bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)] hover:opacity-90 transition-opacity"
        >
          <div className="text-left min-w-0">
            <div className="text-sm font-bold">Enjoying the archive?</div>
            <div className="text-xs font-medium text-black/70">
              Your support helps keep every past puzzle free to replay
            </div>
          </div>
          <div className="shrink-0 inline-flex items-center gap-1.5 text-sm font-black">
            <Coffee size={16} />
            Support
          </div>
        </motion.a>

        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mb-8"
        >
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search topics…"
                className="w-full rounded-2xl border-2 border-border bg-background pl-11 pr-4 py-3.5 font-medium outline-none focus:border-foreground/30 transition-colors"
              />
            </div>
            <button
              type="button"
              onClick={() => setSortPopular(!sortPopular)}
              aria-pressed={sortPopular}
              aria-label="Sort archive by popularity"
              title="Sort by most played"
              className={`shrink-0 rounded-2xl border-2 px-3.5 sm:px-4 py-3.5 font-bold transition-colors inline-flex items-center justify-center gap-2 ${
                sortPopular
                  ? 'border-foreground/30 bg-muted'
                  : 'border-border bg-background hover:bg-muted hover:border-foreground/20'
              }`}
            >
              <TrendingUp size={18} className="shrink-0" />
              <span className="hidden sm:inline">Popular</span>
            </button>
            <button
              type="button"
              onClick={goToRandomUnplayed}
              disabled={loading || topics.length === 0 || unplayedTopics.length === 0}
              aria-label="Play a random topic you haven't finished"
              title={
                unplayedTopics.length === 0 && topics.length > 0
                  ? "You've already played every past topic"
                  : "Play a random topic you haven't finished"
              }
              className="shrink-0 rounded-2xl border-2 border-border bg-background px-3.5 sm:px-4 py-3.5 font-bold hover:bg-muted hover:border-foreground/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-background disabled:hover:border-border inline-flex items-center justify-center gap-2"
            >
              <Dices size={18} className="shrink-0" />
              <span className="hidden sm:inline">Random</span>
            </button>
          </div>
          {!loading && topics.length > 0 && unplayedTopics.length === 0 && (
            <div className="mt-2 text-xs font-semibold text-foreground/50">
              You've already played every past topic.
            </div>
          )}
        </motion.div>

        {loading ? (
          <div className="py-20 text-center text-foreground/50 animate-pulse font-medium">
            Loading archive…
          </div>
        ) : topics.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Calendar size={24} className="text-foreground/50" />
            </div>
            <div className="font-bold text-lg mb-1">No past topics yet</div>
            <div className="text-sm text-foreground/50">Come back after a few daily puzzles drop.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <div className="font-bold text-lg mb-1">No matches</div>
            <div className="text-sm text-foreground/50">Try a different search.</div>
          </div>
        ) : (
          <div className="space-y-8 pb-10">
            {sortPopular ? (
              <motion.section initial={false} animate={{ opacity: 1, y: 0 }}>
                <div className="sticky top-0 z-[1] -mx-1 px-1 py-2 mb-3 bg-background/85 backdrop-blur">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-xs font-black uppercase tracking-[0.2em] text-foreground/45">
                      Most played
                    </h2>
                    <div className="text-[11px] font-bold text-foreground/30 tabular-nums">
                      {listed.length}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {listed.map((t, i) => (
                    <motion.div
                      key={t.id}
                      initial={false}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Math.min(0.08 + i * 0.015, 0.4) }}
                    >
                      <ArchiveTopicRow topic={t} rank={i + 1} siblings={topics} />
                    </motion.div>
                  ))}
                </div>
              </motion.section>
            ) : (
              groups.map((group, gi) => (
                <motion.section
                  key={group.key}
                  initial={false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(0.15 + gi * 0.04, 0.4) }}
                >
                  <div className="sticky top-0 z-[1] -mx-1 px-1 py-2 mb-3 bg-background/85 backdrop-blur">
                    <div className="flex items-center justify-between gap-3">
                      <h2 className="text-xs font-black uppercase tracking-[0.2em] text-foreground/45">
                        {group.label}
                      </h2>
                      <div className="text-[11px] font-bold text-foreground/30 tabular-nums">
                        {group.topics.length}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {group.topics.map((t, i) => (
                      <motion.div
                        key={t.id}
                        initial={false}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: Math.min(0.18 + gi * 0.04 + i * 0.02, 0.5) }}
                      >
                        <ArchiveTopicRow topic={t} siblings={topics} />
                      </motion.div>
                    ))}
                  </div>
                </motion.section>
              ))
            )}
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
    </NarrowPage>
  );
}
